import { idbGet, idbSet } from "../cache/idb.js";

let hdf5Mod = null;

async function getHdf5() {
  if (!hdf5Mod) hdf5Mod = await import("jsfive");
  return hdf5Mod;
}

export const GLM_START_YMD = 20180214;
export const GLM_EAST_SWITCH_YMD = 20250407;
export const GLM_WEST_SWITCH_YMD = 20230104;
export const GLM_G17_START_YMD = 20190212;
export const GLM_WINDOW_SEC = 30 * 60;
export const GLM_MAX_FLASHES = 7200;
export const GLM_OPACITY_NEW = 0.95;
export const GLM_OPACITY_OLD = 0.2;
export const GLM_BOLT_SIZE_NEW = 13;
export const GLM_BOLT_SIZE_OLD = 10.5;

const GCS_API = "https://storage.googleapis.com/storage/v1/b";
const FETCH_CONCURRENCY = 8;

const listCache = new Map();
const flashCache = new Map();

export function ymdNumber(date) {
  const digits = String(date || "").replace(/-/g, "").slice(0, 8);
  const n = parseInt(digits, 10);
  return Number.isFinite(n) ? n : null;
}

export function glmAvailableOn(date) {
  const ymd = ymdNumber(date);
  return ymd != null && ymd >= GLM_START_YMD;
}

export function selectGlmSatellites(date, lon) {
  const ymd = ymdNumber(date);
  if (ymd == null || ymd < GLM_START_YMD) return [];
  const east = ymd >= GLM_EAST_SWITCH_YMD
    ? { id: "G19", bucket: "gcp-public-data-goes-19" }
    : { id: "G16", bucket: "gcp-public-data-goes-16" };
  const west = ymd >= GLM_WEST_SWITCH_YMD
    ? { id: "G18", bucket: "gcp-public-data-goes-18" }
    : ymd >= GLM_G17_START_YMD
      ? { id: "G17", bucket: "gcp-public-data-goes-17" }
      : null;
  if (!Number.isFinite(lon) || lon >= -100) return [east];
  if (lon <= -110) return west ? [west] : [east];
  return west ? [east, west] : [east];
}

export function fallbackSatellite(sat) {
  if (!sat) return null;
  if (sat.id === "G19") return { id: "G16", bucket: "gcp-public-data-goes-16" };
  if (sat.id === "G16") return { id: "G19", bucket: "gcp-public-data-goes-19" };
  if (sat.id === "G18") return { id: "G17", bucket: "gcp-public-data-goes-17" };
  if (sat.id === "G17") return { id: "G18", bucket: "gcp-public-data-goes-18" };
  return null;
}

export function parseUtcScanMs(date, time) {
  const d = String(date || "").replace(/-/g, "");
  const t = String(time || "").replace(/:/g, "");
  if (d.length < 8 || t.length < 4) return null;
  const y = parseInt(d.slice(0, 4), 10);
  const mo = parseInt(d.slice(4, 6), 10) - 1;
  const da = parseInt(d.slice(6, 8), 10);
  const hh = parseInt(t.slice(0, 2), 10);
  const mm = parseInt(t.slice(2, 4), 10);
  const ss = t.length >= 6 ? parseInt(t.slice(4, 6), 10) : 0;
  const ms = Date.UTC(y, mo, da, hh, mm, ss);
  return Number.isFinite(ms) ? ms : null;
}

export function utcDayOfYear(ms) {
  const dt = new Date(ms);
  const start = Date.UTC(dt.getUTCFullYear(), 0, 0);
  return Math.floor((ms - start) / 86400000);
}

export function glmHourPrefix(ms) {
  const dt = new Date(ms);
  const year = dt.getUTCFullYear();
  const doy = String(utcDayOfYear(ms)).padStart(3, "0");
  const hour = String(dt.getUTCHours()).padStart(2, "0");
  return `GLM-L2-LCFA/${year}/${doy}/${hour}/`;
}

export function hourPrefixesBetween(startMs, endMs) {
  const prefixes = [];
  const first = new Date(startMs);
  let t = Date.UTC(first.getUTCFullYear(), first.getUTCMonth(), first.getUTCDate(), first.getUTCHours());
  const last = new Date(endMs);
  const endHour = Date.UTC(last.getUTCFullYear(), last.getUTCMonth(), last.getUTCDate(), last.getUTCHours());
  while (t <= endHour) {
    prefixes.push(glmHourPrefix(t));
    t += 3600000;
  }
  return prefixes;
}

export function glmStampToMs(stamp) {
  const s = String(stamp || "");
  if (s.length < 14) return null;
  const year = parseInt(s.slice(0, 4), 10);
  const doy = parseInt(s.slice(4, 7), 10);
  const hh = parseInt(s.slice(7, 9), 10);
  const mm = parseInt(s.slice(9, 11), 10);
  const ss = parseInt(s.slice(11, 13), 10);
  const tenth = s.length >= 15 ? parseInt(s.slice(14, 15), 10) : 0;
  const ms = Date.UTC(year, 0, 1) + (doy - 1) * 86400000 + (((hh * 3600) + (mm * 60) + ss) * 1000) + (tenth * 100);
  return Number.isFinite(ms) ? ms : null;
}

export function parseGlmObjectName(name) {
  const base = String(name || "").split("/").pop() || "";
  const match = base.match(/_s(\d{14,15})_e(\d{14,15})/);
  if (!match) return null;
  return {
    name,
    startMs: glmStampToMs(match[1]),
    endMs: glmStampToMs(match[2]),
  };
}

export function gcsListUrl(bucket, prefix, pageToken) {
  const params = new URLSearchParams({
    prefix,
    maxResults: "1000",
    fields: "items(name,size),nextPageToken",
  });
  if (pageToken) params.set("pageToken", pageToken);
  return `${GCS_API}/${bucket}/o?${params}`;
}

export function gcsObjectUrl(bucket, name) {
  return `${GCS_API}/${bucket}/o/${encodeURIComponent(name)}?alt=media`;
}

export function haversineKm(lat1, lon1, lat2, lon2) {
  const toRad = (deg) => (deg * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a = (Math.sin(dLat / 2) ** 2)
    + (Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * (Math.sin(dLon / 2) ** 2));
  return 2 * 6371 * Math.asin(Math.min(1, Math.sqrt(a)));
}

export function filterFlashesInRange(flashes, lat, lon, maxRangeKm) {
  const range = Number(maxRangeKm);
  const limit = Number.isFinite(range) && range > 0 ? range : 230;
  const latPad = limit / 111;
  const cosLat = Math.cos((lat * Math.PI) / 180);
  const lonPad = limit / (111 * (Math.abs(cosLat) > 0.01 ? cosLat : 0.01));
  return (flashes || []).filter((flash) => {
    if (!Number.isFinite(flash.lat) || !Number.isFinite(flash.lon)) return false;
    if (Math.abs(flash.lat - lat) > latPad || Math.abs(flash.lon - lon) > lonPad) return false;
    return haversineKm(lat, lon, flash.lat, flash.lon) <= limit;
  });
}

export function filterFlashesInWindow(flashes, centerMs, windowSec = GLM_WINDOW_SEC) {
  if (!Number.isFinite(centerMs)) return flashes || [];
  const half = (Number(windowSec) || GLM_WINDOW_SEC) * 1000;
  return (flashes || []).filter((flash) => (
    !Number.isFinite(flash.timeMs) || Math.abs(flash.timeMs - centerMs) <= half
  ));
}

function displayWindowSec(windowSec) {
  const span = Number(windowSec);
  return Number.isFinite(span) && span > 0 ? span : GLM_WINDOW_SEC;
}

export function flashAgeSec(flash, frameMs) {
  if (!Number.isFinite(flash?.timeMs) || !Number.isFinite(frameMs)) return 0;
  return (frameMs - flash.timeMs) / 1000;
}

export function flashOpacity(ageSec, windowSec = GLM_WINDOW_SEC, options = {}) {
  const hideFuture = !!options.hideFuture;
  const span = displayWindowSec(windowSec);
  const age = Number(ageSec);
  if (!Number.isFinite(age)) return GLM_OPACITY_NEW;
  if (age < 0) return hideFuture ? 0 : GLM_OPACITY_NEW;
  const t = Math.max(0, Math.min(1, age / span));
  return GLM_OPACITY_NEW + ((GLM_OPACITY_OLD - GLM_OPACITY_NEW) * t);
}

export function flashBoltSizePx(ageSec, windowSec = GLM_WINDOW_SEC) {
  const span = displayWindowSec(windowSec);
  const age = Number(ageSec);
  if (!Number.isFinite(age) || age <= 0) return GLM_BOLT_SIZE_NEW;
  const t = Math.max(0, Math.min(1, age / span));
  return GLM_BOLT_SIZE_NEW + ((GLM_BOLT_SIZE_OLD - GLM_BOLT_SIZE_NEW) * t);
}

export function filterFlashesForDisplay(flashes, frameMs, options = {}) {
  const hideFuture = !!options.hideFuture;
  const windowMs = displayWindowSec(options.windowSec) * 1000;
  return (flashes || []).filter((flash) => {
    if (!Number.isFinite(flash.timeMs) || !Number.isFinite(frameMs)) return true;
    const ageMs = frameMs - flash.timeMs;
    if (hideFuture) return ageMs >= 0 && ageMs <= windowMs;
    return Math.abs(ageMs) <= windowMs;
  });
}

export function glmInfoLabel({ loading = false, count = null } = {}) {
  const hasCount = count != null && Number.isFinite(Number(count));
  if (loading) return hasCount ? `${Number(count)}…` : "loading";
  return hasCount ? String(count) : null;
}

export function dedupeFlashes(flashes) {
  const seen = new Set();
  const out = [];
  for (const flash of flashes || []) {
    const timeKey = Number.isFinite(flash.timeMs) ? Math.round(flash.timeMs / 2000) : "";
    const key = `${flash.lat.toFixed(3)}|${flash.lon.toFixed(3)}|${timeKey}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(flash);
  }
  return out;
}

export function capFlashes(flashes, max = GLM_MAX_FLASHES) {
  const list = flashes || [];
  if (list.length <= max) return list;
  return list
    .slice()
    .sort((a, b) => (b.energy || 0) - (a.energy || 0))
    .slice(0, max);
}

function firstNumber(value, fallback) {
  const raw = Array.isArray(value) ? value[0] : value;
  const n = Number(raw);
  return Number.isFinite(n) ? n : fallback;
}

export function packedNumber(raw, attrs = {}) {
  if (!Number.isFinite(raw)) return null;
  let value = raw;
  if (String(attrs._Unsigned).toLowerCase() === "true" && raw < 0) {
    value = raw + 65536;
  }
  return (value * firstNumber(attrs.scale_factor, 1)) + firstNumber(attrs.add_offset, 0);
}

export function parseSecondsSinceEpoch(units) {
  const match = String(units || "").match(
    /seconds since\s+(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2}):(\d{2})/,
  );
  if (!match) return null;
  return Date.UTC(
    parseInt(match[1], 10),
    parseInt(match[2], 10) - 1,
    parseInt(match[3], 10),
    parseInt(match[4], 10),
    parseInt(match[5], 10),
    parseInt(match[6], 10),
  );
}

function toArrayBuffer(input) {
  if (input instanceof ArrayBuffer) return input;
  if (ArrayBuffer.isView(input)) {
    return input.buffer.slice(input.byteOffset, input.byteOffset + input.byteLength);
  }
  throw new Error("Expected ArrayBuffer");
}

export function extractFlashesFromHdf(file, { filename = "" } = {}) {
  const latDs = file.get("flash_lat");
  const lonDs = file.get("flash_lon");
  if (!latDs || !lonDs) return [];
  const lats = Array.from(latDs.value || []);
  const lons = Array.from(lonDs.value || []);
  const count = Math.min(lats.length, lons.length);
  const timeDs = file.get("flash_time_offset_of_first_event");
  const energyDs = file.get("flash_energy");
  const idDs = file.get("flash_id");
  const qualityDs = file.get("flash_quality_flag");
  const timeAttrs = timeDs?.attrs || {};
  const epochMs = parseSecondsSinceEpoch(timeAttrs.units);
  const fileMeta = parseGlmObjectName(filename);
  const flashes = [];
  for (let i = 0; i < count; i++) {
    const lat = Number(lats[i]);
    const lon = Number(lons[i]);
    if (!Number.isFinite(lat) || !Number.isFinite(lon)) continue;
    if (Math.abs(lat) > 90 || Math.abs(lon) > 180) continue;
    const quality = qualityDs?.value?.[i];
    if (quality != null && Number(quality) !== 0) continue;
    let timeMs = null;
    if (timeDs?.value) {
      const seconds = packedNumber(Number(timeDs.value[i]), timeAttrs);
      if (seconds != null && epochMs != null) timeMs = epochMs + (seconds * 1000);
    }
    if (timeMs == null && fileMeta?.startMs) timeMs = fileMeta.startMs;
    const rawId = idDs?.value ? Number(idDs.value[i]) : i;
    flashes.push({
      lat,
      lon,
      timeMs,
      energy: energyDs?.value ? Number(energyDs.value[i]) : null,
      id: rawId < 0 ? rawId + 65536 : rawId,
    });
  }
  return flashes;
}

export async function parseGlmFlashesLocal(buffer, filename) {
  const hdf5 = await getHdf5();
  const file = new hdf5.File(toArrayBuffer(buffer), filename || "glm.nc");
  return extractFlashesFromHdf(file, { filename });
}

export async function parseGlmFlashes(buffer, filename, options = {}) {
  const inBrowser = typeof window !== "undefined";
  const useWorker = options.worker === true
    || (options.worker !== false && inBrowser && typeof Worker !== "undefined");
  if (useWorker && typeof Worker !== "undefined") {
    try {
      const { parseGlmInWorker } = await import("../decode/workers.js");
      const source = buffer instanceof Uint8Array ? buffer : new Uint8Array(toArrayBuffer(buffer));
      return await parseGlmInWorker(source.slice(), filename);
    } catch {
      // Worker unavailable or failed — parse on this thread.
    }
  }
  return parseGlmFlashesLocal(buffer, filename);
}

export function clearGlmCaches() {
  listCache.clear();
  flashCache.clear();
}

async function listGcsPrefixUncached(bucket, prefix, { signal } = {}) {
  const items = [];
  let pageToken = "";
  do {
    const resp = await fetch(gcsListUrl(bucket, prefix, pageToken), { signal });
    if (!resp.ok) throw new Error(`GLM list failed (${resp.status})`);
    const json = await resp.json();
    for (const item of json.items || []) {
      if (item?.name) items.push({ name: item.name, size: parseInt(item.size || "0", 10) });
    }
    pageToken = json.nextPageToken || "";
  } while (pageToken);
  return items;
}

async function listGcsPrefix(bucket, prefix, { signal, skipCache = false } = {}) {
  const cacheKey = `${bucket}:${prefix}`;
  if (!skipCache && listCache.has(cacheKey)) return listCache.get(cacheKey);
  const pending = listGcsPrefixUncached(bucket, prefix, { signal });
  if (!skipCache) listCache.set(cacheKey, pending);
  try {
    return await pending;
  } catch (err) {
    if (!skipCache) listCache.delete(cacheKey);
    throw err;
  }
}

async function fetchGlmBytes(bucket, name, { signal } = {}) {
  const resp = await fetch(gcsObjectUrl(bucket, name), { signal });
  if (!resp.ok) throw new Error(`GLM get failed (${resp.status})`);
  return new Uint8Array(await resp.arrayBuffer());
}

async function flashesForObject(bucket, name, { signal, worker, skipCache = false } = {}) {
  const cacheKey = `${bucket}:${name}`;
  if (!skipCache && flashCache.has(cacheKey)) return flashCache.get(cacheKey);
  const parsedKey = `glmflashes:${bucket}:${name}`;
  const work = (async () => {
    if (!skipCache) {
      const cached = await idbGet(parsedKey);
      if (Array.isArray(cached?.value)) return cached.value;
    }
    const bytes = await fetchGlmBytes(bucket, name, { signal });
    const flashes = await parseGlmFlashes(bytes, name, { worker });
    if (!skipCache) {
      idbSet(parsedKey, flashes, Math.max(256, flashes.length * 48)).catch(() => {});
    }
    return flashes;
  })();
  if (!skipCache) flashCache.set(cacheKey, work);
  try {
    return await work;
  } catch (err) {
    if (!skipCache) flashCache.delete(cacheKey);
    throw err;
  }
}

async function mapPool(items, limit, fn) {
  const out = new Array(items.length);
  let next = 0;
  async function worker() {
    while (next < items.length) {
      const index = next++;
      out[index] = await fn(items[index], index);
    }
  }
  const workers = Array.from({ length: Math.min(limit, items.length) }, () => worker());
  await Promise.all(workers);
  return out;
}

export function glmFileMidMs(file) {
  const meta = parseGlmObjectName(file?.name);
  if (!meta || !Number.isFinite(meta.startMs) || !Number.isFinite(meta.endMs)) return null;
  return (meta.startMs + meta.endMs) / 2;
}

export function sortGlmFilesNearestFirst(files, centerMs) {
  if (!Number.isFinite(centerMs)) return files || [];
  return (files || []).slice().sort((a, b) => {
    const aMid = glmFileMidMs(a);
    const bMid = glmFileMidMs(b);
    const aDist = aMid == null ? Number.POSITIVE_INFINITY : Math.abs(aMid - centerMs);
    const bDist = bMid == null ? Number.POSITIVE_INFINITY : Math.abs(bMid - centerMs);
    return aDist - bDist;
  });
}

function objectsOverlappingWindow(items, startMs, endMs) {
  return items.filter((item) => {
    const meta = parseGlmObjectName(item.name);
    if (!meta || !Number.isFinite(meta.startMs) || !Number.isFinite(meta.endMs)) return true;
    return meta.endMs >= startMs && meta.startMs <= endMs;
  });
}

async function listSatelliteFiles(sat, prefixes, startMs, endMs, { signal, skipCache = false } = {}) {
  const pages = await Promise.all(prefixes.map((prefix) => (
    listGcsPrefix(sat.bucket, prefix, { signal, skipCache })
  )));
  return objectsOverlappingWindow(pages.flat(), startMs, endMs).map((item) => ({
    ...item,
    bucket: sat.bucket,
    sat: sat.id,
  }));
}

export async function loadGlmFlashes({
  lat,
  lon,
  maxRangeKm,
  date,
  time,
  windowSec = GLM_WINDOW_SEC,
  signal,
  onPartial,
  worker,
  skipCache = false,
} = {}) {
  if (!glmAvailableOn(date)) {
    return { flashes: [], reason: "unavailable", satellites: [] };
  }
  const centerMs = parseUtcScanMs(date, time);
  if (!Number.isFinite(lat) || !Number.isFinite(lon) || centerMs == null) {
    return { flashes: [], reason: "empty", satellites: [] };
  }

  const half = (Number(windowSec) || GLM_WINDOW_SEC) * 1000;
  const startMs = centerMs - half;
  const endMs = centerMs + half;
  const prefixes = hourPrefixesBetween(startMs, endMs);
  const primary = selectGlmSatellites(date, lon);
  if (!primary.length) {
    return { flashes: [], reason: "unavailable", satellites: [] };
  }

  let files = [];
  const used = [];
  try {
    for (const sat of primary) {
      let satFiles = await listSatelliteFiles(sat, prefixes, startMs, endMs, { signal, skipCache });
      if (!satFiles.length) {
        const fallback = fallbackSatellite(sat);
        if (fallback && !primary.some((item) => item.id === fallback.id)) {
          satFiles = await listSatelliteFiles(fallback, prefixes, startMs, endMs, { signal, skipCache });
          if (satFiles.length) used.push(fallback);
        }
      } else {
        used.push(sat);
      }
      files = files.concat(satFiles);
    }
  } catch (err) {
    if (err?.name === "AbortError") throw err;
    return { flashes: [], reason: "error", satellites: used, message: err.message };
  }

  if (!files.length) {
    return { flashes: [], reason: "empty", satellites: used };
  }

  files = sortGlmFilesNearestFirst(files, centerMs);

  const collected = [];
  try {
    await mapPool(files, FETCH_CONCURRENCY, async (file) => {
      if (signal?.aborted) throw new DOMException("Aborted", "AbortError");
      const parsed = await flashesForObject(file.bucket, file.name, { signal, worker, skipCache });
      collected.push(...parsed);
      if (onPartial) {
        onPartial(prepareFlashes(collected, lat, lon, maxRangeKm, centerMs, windowSec));
      }
    });
  } catch (err) {
    if (err?.name === "AbortError") throw err;
    if (!collected.length) {
      return { flashes: [], reason: "error", satellites: used, message: err.message };
    }
  }

  return {
    flashes: prepareFlashes(collected, lat, lon, maxRangeKm, centerMs, windowSec),
    reason: "ok",
    satellites: used,
  };
}

function prepareFlashes(flashes, lat, lon, maxRangeKm, centerMs, windowSec) {
  return capFlashes(dedupeFlashes(
    filterFlashesInWindow(filterFlashesInRange(flashes, lat, lon, maxRangeKm), centerMs, windowSec),
  ));
}
